// @flow
import React, { useState } from 'react'
import styled from 'styled-components'
import get from 'lodash/get'
import { useQuery } from 'react-query'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ReactTooltip from 'react-tooltip'

import spinner from '../assets/img/spinner.gif'

import { colors } from '../styles'
import { getSamples } from '../api'

import type { Sample } from '../types'

const SPEED = '200ms'

type Props = {
  id: string,
  title: string,
  samples: Array<Sample>,
  onAddItem: Function,
}

const StyledTooltip = styled(ReactTooltip)`
  background: ${colors.darkGray} !important;
  color: #fff !important;
  opacity: 1 !important;
  z-index: 1000000 !important;
`

const SampleContent = ({ sampleGroupId, onAddItem }) => {

  const { isLoading, data } = useQuery(`getSample${sampleGroupId}`, () => getSamples(sampleGroupId))

  // console.log('SampleContent', sampleGroupId, isLoading, data)

  if (isLoading) {
    return <Img src={spinner} />
  }
  const group = data.group

  const samples = get(data, 'group.samples', [])

  if (!samples || samples.length === 0) {
    return (
      <p><em>No samples available.</em></p>
    )
  }

  return (
    <Thumbnails>
      {samples.map(sample => {
        const internalLabel = get(sample, 'title', sample.label)
        const externalLabel = get(sample, 'external_label') || sample.label
        // console.log('thumbnail labels', { label: sample.label, internalLabel, externalLabel, sample })
        const item = {
          key: sample.id,
          label: externalLabel,
          imageUrl: sample.imageUrl,
          attributes: sample.attributes,
        }
        return (
          <Thumbnail key={`thumbnail-${item.key}`} data-tip={internalLabel}>
            <Add onClick={(evt) => onAddItem(evt, item, group)}>
              <FontAwesomeIcon icon={[ 'fas', 'plus-circle' ]} />
            </Add>
            <img alt={`thumbnail-${item.key}`} src={item.imageUrl} />
          </Thumbnail>
        )
      })}
      <StyledTooltip data-background-color={colors.darkGray} data-border-color={colors.darkGray} data-type="info" data-effect="solid" />

    </Thumbnails>
  )
}

const SampleGroup = ({ id, title, onAddItem }: Props): Function => {
  const [ isOpened, setOpen ] = useState(false)

  const toggle = () => setOpen(!isOpened)

  return (
    <Panel>
      <TitleToggle onClick={toggle}>
        <Title className={`state--${isOpened ? 'open' : 'closed'}`}>
          {title}
          <FontAwesomeIcon icon={[ 'fas', 'angle-down' ]} />
        </Title>
      </TitleToggle>
      <Content className={`state--${isOpened ? 'open' : 'closed'}`}>
        <hr />
        {isOpened && <SampleContent onAddItem={onAddItem} sampleGroupId={id} />}
      </Content>
    </Panel>

  )
}

export default SampleGroup

const Panel = styled.div`
  background: #fff;
  border: 1px solid ${colors.midGray};
  color: ${colors.midGray};
  padding: 0;
  // background: ${colors.lightGray};
  // padding: 1rem;
  // border-right: 2px solid ${colors.midGray};
`

const Img = styled.img`
  text-align: center;
`

const TitleToggle = styled.button`
  background: none;
  border: none;
  width: 100%;
`

const Title = styled.h4`
  margin: 0;
  padding-top: 0.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${colors.midLtGray};
  text-align: left;
  
  svg {
    float: right;
    color: ${colors.midGray};
    transition: all ${SPEED} linear;   
  }
  
  &.state--open svg {
    transform: rotateX(180deg);
    transition: all ${SPEED} linear;
  }
`

const Content = styled.div`
  background: #fff;
  margin-top: 0;
  padding: 0 1rem;
  
  overflow: hidden;
  max-height: 0;
  transition: all ${SPEED} linear;   
  
  &.state--open {
    min-height: 150px;
    max-height: 1000rem;
    transition: all ${SPEED} linear;
  }
  
  hr {
    border: 1px solid ${colors.lightGray};
    width: 120%;
    margin: 0 -10%;
  }
`

const Thumbnails = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-left: -1rem;
  margin-right: -1rem;
`

const Add = styled.button`
  background: none;
  border: none;
  display: inline-block;
  position: absolute;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  transform: scale(0);
  transition: all 0.1s ease-in-out;
  
  & [data-icon="plus-circle"] {
    font-size: 32px;
    width: 32px;
    height: 32px;
    color: #444;
    background: white;
    border: 1px solid rgba(0,0,0,0.5);
    box-shadow: 0 0 15px rgb(0 0 0 / 50%);
    border-radius: 50%;    
  }
`

const Thumbnail = styled.div`
  position: relative;
  width: 33%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem;

  img {
    width: 100%;
    object-fit: contain;
  }

  &:hover ${Add}, &:active ${Add}, &:focus ${Add} {
    transform: scale(1);
    transition: all 0.1s ease-in-out;
  }
}
`

